import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CalendarCheck, Clock, Video, MapPin, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { formatDeShort } from "@/lib/date";

const WEEKDAYS = ["Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"];
const formatDeLong = (iso) => {
  const d = iso ? new Date(`${iso}T00:00:00`) : null;
  return d && !isNaN(d.getTime()) ? `${WEEKDAYS[d.getDay()]}, ${formatDeShort(iso)}` : formatDeShort(iso);
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TerminBestaetigungPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [rejected, setRejected] = useState({});
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    axios.get(`${API}/appointments/invite/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Link konnte nicht geladen werden"));
  }, [token]);

  const open = (data?.appointments || []).filter((a) => ["proposed", "accepted"].includes(a.status));

  const submit = async () => {
    const reject = open.filter((a) => rejected[a.appointment_id]).map((a) => a.appointment_id);
    const accept = open.filter((a) => !rejected[a.appointment_id]).map((a) => a.appointment_id);
    setSaving(true);
    try {
      const { data: res } = await axios.post(`${API}/appointments/invite/${token}/respond`, { accept, reject });
      setDone(res);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally { setSaving(false); }
  };

  if (error) return <Shell><p className="text-sm text-red-700" data-testid="invite-error">{error}</p></Shell>;
  if (!data) return <Shell><p className="text-sm text-slate-500">Lädt…</p></Shell>;

  if (done) {
    return (
      <Shell>
        <div className="text-center py-6" data-testid="invite-done">
          <CheckCircle2 size={40} strokeWidth={1} className="mx-auto text-emerald-600 mb-4" />
          <h2 className="font-heading text-2xl font-light tracking-tight text-slate-900">Vielen Dank!</h2>
          <p className="text-sm text-slate-600 mt-2">
            {done.accepted} Termin{done.accepted === 1 ? "" : "e"} bestätigt
            {done.rejected ? ` · ${done.rejected} abgesagt – dein Coach meldet sich mit einem neuen Vorschlag` : ""}.
          </p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="mb-6">
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 flex items-center gap-1.5">
          <CalendarCheck size={11} strokeWidth={1.5} /> Terminbestätigung
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl font-light tracking-tighter text-slate-900 mt-2">
          Hallo {data.client_name || ""}
        </h1>
        <p className="text-sm text-slate-600 mt-2">
          {data.coach_name ? `${data.coach_name} hat` : "Wir haben"} folgende Coachingtermine für dich geplant.
          Hake alle Termine ab, die <b>nicht</b> passen – der Rest gilt als bestätigt.
        </p>
      </div>

      <div className="divide-y divide-slate-100 border border-slate-200 bg-white" data-testid="invite-appointments">
        {(data.appointments || []).map((a) => {
          const ModeIcon = a.mode === "online" ? Video : MapPin;
          const closed = !["proposed", "accepted"].includes(a.status);
          const isRejected = !!rejected[a.appointment_id];
          return (
            <div key={a.appointment_id} className={`p-4 flex items-center gap-4 ${isRejected ? "bg-red-50/60" : ""}`} data-testid={`invite-row-${a.appointment_id}`}>
              <div className="flex-1">
                <div className={`text-sm font-medium text-slate-900 ${isRejected ? "line-through" : ""}`}>{formatDeLong(a.date)}</div>
                <div className="text-xs text-slate-600 font-mono mt-0.5 flex items-center gap-3 flex-wrap">
                  {(a.time_from || a.time_to) && <span className="flex items-center gap-1"><Clock size={11} strokeWidth={1.5} />{a.time_from}–{a.time_to}</span>}
                  <span>{a.planned_ue} UE</span>
                  <span className="flex items-center gap-1"><ModeIcon size={11} strokeWidth={1.5} />{a.mode === "online" ? "Remote" : "Präsenz"}</span>
                  {a.topic && <span className="italic">{a.topic}</span>}
                </div>
              </div>
              {closed ? (
                <span className="text-[10px] font-bold uppercase tracking-[0.2em] px-2 py-1 bg-slate-100 text-slate-600">
                  {a.status === "rejected" ? "abgesagt" : "erledigt"}
                </span>
              ) : (
                <button
                  onClick={() => setRejected((p) => ({ ...p, [a.appointment_id]: !p[a.appointment_id] }))}
                  data-testid={`invite-toggle-${a.appointment_id}`}
                  className={`px-3 py-1.5 text-xs font-medium border flex items-center gap-1.5 ${isRejected ? "bg-red-600 text-white border-red-600" : "border-slate-300 text-slate-600 hover:bg-slate-100"}`}
                >
                  <XCircle size={12} strokeWidth={1.5} /> {isRejected ? "Abgesagt" : "Passt nicht"}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {open.length > 0 && (
        <button onClick={submit} disabled={saving} data-testid="invite-submit"
          className="mt-6 w-full px-4 py-3.5 bg-slate-900 text-white text-sm font-medium hover:bg-black disabled:opacity-50 flex items-center justify-center gap-2">
          <CheckCircle2 size={14} strokeWidth={1.5} />
          {saving ? "Wird gespeichert…" : (() => {
            const n = open.length - Object.values(rejected).filter(Boolean).length;
            return n === 1 ? "1 Termin bestätigen" : `${n} Termine bestätigen`;
          })()}
        </button>
      )}
    </Shell>
  );
}

const Shell = ({ children }) => (
  <div className="min-h-screen bg-slate-50 px-4 py-12">
    <div className="max-w-[640px] mx-auto">{children}</div>
  </div>
);
